import type { AgentHistoryEventWire, AgentId, LlmRoutePurposeAny } from '@developerz/domain';
import { A } from '@solidjs/router';
import { createSignal, For, type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { AGENT_ROUTE_PURPOSE } from '../lib/agents';
import { useAgentHistory } from '../lib/agents-history';
import { orgHref } from '../lib/org';
import { SESSION_TOKEN } from '../lib/session';
import { ago } from '../lib/time';
import { Popover } from './Popover';

// One agent's "who changed this last" chip (#5209 g3, epic #5200) — a
// hairline-ringed avatar, @login, relative time, and a channel glyph, ALWAYS
// showing the latest change (Card anatomy); tap opens a vertical timeline of
// the last 5 before → after changes plus a link to the full Audit trail.
// Read-only: the timeline is `GET /v1/agents/history`, and nothing here writes.
//
// The trigger itself needs the latest row (not just the panel), so the read is
// eager, not deferred to the tap — one small GET per card, once per mount, no
// poll. One request per card is cheap enough that gating it behind the first tap
// would only cost a second round-trip the trigger's own content needs anyway.

/**
 * "@login" for a member's own change; the system text, un-prefixed, for a
 * credential with no member behind it (`actor: null`); a distinct "former
 * member" text when the member row IS there but its `login` is gone
 * (`actor.login: null`, e.g. a deleted account) — that member made a real
 * change and is not "the system".
 */
function actorLabel(row: AgentHistoryEventWire): string {
  if (row.actor === null) return t('agents.changed_by.system');
  if (row.actor.login === null) return t('agents.changed_by.former_member');
  return `@${row.actor.login}`;
}

/**
 * A recorded pin's own value, in `pinTriggerValue`'s order (key label, then
 * provider/model, then either half alone) so the timeline names a pin exactly as
 * the card's trigger does. A recorded pin naming none of those pins no model, so
 * it IS Automatic — the one place that word is stated from the data.
 */
function pinLabel(pin: NonNullable<AgentHistoryEventWire['before']>): string {
  if (pin.key_label !== null) return pin.key_label;
  if (pin.provider !== null && pin.model !== null) return `${pin.provider}/${pin.model}`;
  return pin.model ?? pin.provider ?? t('agents.automatic');
}

/** A mode row: on / off / reset — `enabled: null` on a `.set` is unrecorded. */
function modeSummary(row: AgentHistoryEventWire): string {
  if (row.event === 'agent_mode.cleared') return t('agents.changed_by.mode_reset');
  if (row.enabled === true) return t('agents.changed_by.mode_on');
  if (row.enabled === false) return t('agents.changed_by.mode_off');
  return t('agents.changed_by.mode_set_unrecorded');
}

/**
 * The row's own text. `null` on `before`/`after`/`enabled` means "this row does
 * not record it" (the wire doc lists pin creation, a mode event and a pre-#5208
 * row under the same null), so it renders as "not recorded" — never a default:
 *
 * - `agent_mode.cleared` (always `enabled: null`) means the override was removed
 *   and the inherited default applies, which can leave the mode ON: "Reset to the
 *   default", never "Turned off". Only `agent_mode.set` with `enabled: false` is.
 * - `llm_route.cleared` names only what was removed. What applies afterwards is
 *   not on the row: an org clear leaves Automatic, but a REPO clear falls back to
 *   the org pin, so "→ Automatic" would be false there.
 * - `llm_route.set` with both halves null is a pre-#5208 row: "Model pin changed".
 */
function rowSummary(row: AgentHistoryEventWire): string {
  if (row.mode_key !== null) return modeSummary(row);
  if (row.event === 'llm_route.cleared') {
    return row.before === null
      ? t('agents.changed_by.route_cleared_unrecorded')
      : t('agents.changed_by.route_cleared', { before: pinLabel(row.before) });
  }
  if (row.before === null && row.after === null) {
    return t('agents.changed_by.route_set_unrecorded');
  }
  const label = (pin: AgentHistoryEventWire['before']): string =>
    pin === null ? t('agents.changed_by.not_recorded') : pinLabel(pin);
  return t('agents.changed_by.route_change', {
    before: label(row.before),
    after: label(row.after),
  });
}

/**
 * A translated label per {@link LlmRoutePurposeAny} — total over the closed
 * route-purpose enumeration, so a purpose added to the vocabulary is a
 * compile error here until it gets a label. `scopeNote` reads this rather
 * than interpolating `row.purpose` directly: the raw wire id (`ci-triage`,
 * `debugging-qa`) is exactly the untranslated-enum-on-screen defect an
 * earlier review pass on this page already removed once (#5209, `AgentCard`
 * pin line, commit 9ac0069).
 *
 * The PIN-ONLY rows are in it too (`triage`, the judge's own pin, `screen` and
 * `escalation`): a change to one of them names its lane, never the raw id.
 */
const PURPOSE_LABEL_KEY: Readonly<Record<LlmRoutePurposeAny, string>> = {
  coding: 'agents.changed_by.purpose.coding',
  'debugging-qa': 'agents.changed_by.purpose.debugging-qa',
  general: 'agents.changed_by.purpose.general',
  quick: 'agents.changed_by.purpose.quick',
  scout: 'agents.changed_by.purpose.scout',
  plan: 'agents.changed_by.purpose.plan',
  'ci-triage': 'agents.changed_by.purpose.ci-triage',
  review: 'agents.changed_by.purpose.review',
  gardener: 'agents.changed_by.purpose.gardener',
  default: 'agents.changed_by.purpose.default',
  escalation: 'agents.changed_by.purpose.escalation',
  triage: 'agents.changed_by.purpose.triage',
  screen: 'agents.changed_by.purpose.screen',
};

/**
 * "repo override" / "{lane} lane" beside a row that is NOT the card's own
 * org-scope, primary-purpose pin — nothing otherwise. Without this, a repo
 * override or a different task-tag lane's change reads as though it were the
 * change to the org pin the card shows above it.
 */
function scopeNote(row: AgentHistoryEventWire, primaryPurpose: string): string | null {
  if (row.scope === 'repo') return t('agents.changed_by.repo_override');
  if (row.purpose !== null && row.purpose !== primaryPurpose) {
    const key = Object.hasOwn(PURPOSE_LABEL_KEY, row.purpose)
      ? PURPOSE_LABEL_KEY[row.purpose as LlmRoutePurposeAny]
      : undefined;
    return t('agents.changed_by.other_lane', { lane: key ? t(key) : row.purpose });
  }
  return null;
}

/**
 * The actor's 16px hairline-ringed avatar: a decoration (`aria-hidden`, the
 * @login beside it is the name), so it adds no text. With a picture it is an
 * `<img alt="">` and NOTHING else is drawn; without one — or once the picture
 * fails to load — the initial rides `data-initials` and CSS paints it, so it
 * never lands in `textContent` or the accessible name. The two are exclusive:
 * the CSS paints through `::before`, which would otherwise sit beside the
 * `<img>` as a second flex item in the same 16px circle. The `broken` fallback
 * mirrors `components/Avatar.tsx`, so a 404 avatar is not left an empty ring.
 */
function Avatar(props: { readonly actor: AgentHistoryEventWire['actor'] }): JSX.Element {
  const [broken, setBroken] = createSignal(false);
  const login = (): string | null => props.actor?.login ?? null;
  const url = (): string | null => (broken() ? null : (props.actor?.avatar_url ?? null));
  return (
    <span
      class="agent-card__avatar"
      aria-hidden="true"
      data-initials={url() ? undefined : login()?.charAt(0).toUpperCase() || undefined}
    >
      <Show when={url()}>
        {(src) => <img src={src()} alt="" loading="lazy" onError={() => setBroken(true)} />}
      </Show>
    </span>
  );
}

export function ChangedByChip(props: {
  readonly agent: AgentId;
  /** The session token: `AgentsView` passes its own (the route's `token`, a test
   * seam like every route's); omitted, it falls back to `SESSION_TOKEN`. */
  readonly token?: string | null;
}): JSX.Element {
  // `=== undefined`, not `??`: an injected `null` MEANS "no session" (mirrors
  // every route's own `token` prop, e.g. `routes/Agents.tsx`).
  const token = props.token === undefined ? SESSION_TOKEN : props.token;
  const history = useAgentHistory(props.agent, true, token);
  const rows = (): readonly AgentHistoryEventWire[] => history.data ?? [];
  const primaryPurpose = AGENT_ROUTE_PURPOSE[props.agent];

  // What the line says when there is no latest row to show (19-7b). A FAILED or
  // refused read (the route needs `read:billing`) says so: an empty list is an
  // ANSWER, and "No changes yet" from no answer at all is a claim about the agent.
  const noRow = (): string => {
    if (history.isError) return t('agents.changed_by.error');
    if (history.isLoading) return t('states.loading');
    return t('agents.changed_by.empty');
  };

  // No `label` on the Popover (19-7a): it becomes an `aria-label`, which REPLACES
  // the trigger's text as its accessible name, so every card announced "Change
  // history" and never who changed it, or when. The visible "Changed by @login ·
  // time · channel" line is the name. "Change history" names the timeline list.
  return (
    <Show
      when={rows()[0]}
      // Nothing to open: plain text, not a button that opens an empty panel.
      fallback={<p class="agent-card__changed-by">{noRow()}</p>}
    >
      {(latest) => (
        <Popover
          triggerClass="agent-card__changed-by agent-card__changed-by--link"
          trigger={
            <>
              <Avatar actor={latest().actor} />
              {t('agents.changed_by.prefix')} <span>{actorLabel(latest())}</span>
              {' · '}
              <span>{ago(latest().at_iso)}</span>
              {/* `via: null` (every pre-#5208 row) means the channel was never
               * recorded: no separator and no channel, never a guessed "on this page". */}
              <Show when={latest().via !== null}>
                {' · '}
                <span class="agent-card__channel" data-via={latest().via ?? 'none'}>
                  {latest().via === 'pat'
                    ? t('agents.changed_by.via_pat')
                    : t('agents.changed_by.via_session')}
                </span>
              </Show>
              <Show when={scopeNote(latest(), primaryPurpose)}>
                {(note) => (
                  <>
                    {' · '}
                    <span class="agent-card__scope-note">{note()}</span>
                  </>
                )}
              </Show>
            </>
          }
        >
          {() => (
            <div class="agent-card__timeline">
              <ul aria-label={t('agents.changed_by.timeline_label')}>
                <For each={rows()}>
                  {(row) => (
                    <li>
                      <span>{actorLabel(row)}</span>
                      {' · '}
                      <span>{ago(row.at_iso)}</span>
                      {' · '}
                      <span>{rowSummary(row)}</span>
                      <Show when={scopeNote(row, primaryPurpose)}>
                        {(note) => (
                          <>
                            {' · '}
                            <span class="agent-card__scope-note">{note()}</span>
                          </>
                        )}
                      </Show>
                    </li>
                  )}
                </For>
              </ul>
              <A href={orgHref('/audit')}>{t('agents.changed_by.full_history')}</A>
            </div>
          )}
        </Popover>
      )}
    </Show>
  );
}
